import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { act } from "@/lib/hooks";
import { Field } from "@/components/common";

function NewCustomerDialog({ name, onClose, onCreated }) {
  const [f, setF] = useState({ name, contact_name: "", phone: "", email: "" });
  const save = async () => {
    const c = await act(() => api.post("/tenant/customers", { name: f.name.trim(), contact_name: f.contact_name || null, phone: f.phone || null, email: f.email || null }), "Customer added");
    if (c) onCreated(c);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-customer-dialog">
        <DialogHeader><DialogTitle>Add new customer</DialogTitle><DialogDescription>Create the customer and keep working on this form.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          {[["name", "Customer name *"], ["contact_name", "Contact name"], ["phone", "Phone"], ["email", "Email"]].map(([k, lab]) => (
            <Field key={k} label={lab}><Input data-testid={`new-customer-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>))}
        </div>
        <DialogFooter><Button data-testid="new-customer-save" disabled={!f.name.trim()} onClick={save}>Save customer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerPicker({ customers, value, onChange, onCreated, testId = "customer-picker", label = (c) => c.name }) {
  const current = customers.find((c) => c.id === value);
  const [q, setQ] = useState(null);
  const [adding, setAdding] = useState(null);
  const text = q ?? (current ? label(current) : "");
  const hits = customers.filter((c) => c.name.toLowerCase().includes((q || "").toLowerCase())).slice(0, 8);
  const exact = customers.some((c) => c.name.toLowerCase() === (q || "").trim().toLowerCase());
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    if (q === null || !ref.current) return;
    const place = () => { const r = ref.current.getBoundingClientRect(); setBox({ top: r.bottom + 4, left: r.left, width: r.width }); };
    place();
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [q]);
  return (
    <div className="relative" ref={ref}>
      <Input data-testid={`${testId}-input`} placeholder="Type to search customers" value={text} onChange={(e) => setQ(e.target.value)} onBlur={() => setTimeout(() => setQ(null), 200)} />
      {q !== null && box && createPortal(
        <div style={{ position: "fixed", top: box.top, left: box.left, width: box.width, pointerEvents: "auto" }} className="z-[1000] max-h-64 overflow-auto rounded-md border bg-white shadow-lg" data-testid={`${testId}-menu`}>
          {hits.map((c) => <button type="button" key={c.id} data-testid={`${testId}-option-${c.id}`} className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onMouseDown={() => { onChange(c.id); setQ(null); }}>{label(c)}</button>)}
          {q.trim() && !exact && <button type="button" data-testid={`${testId}-add-new`} className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm font-medium text-[#13294B] hover:bg-slate-50" onMouseDown={() => setAdding(q.trim())}><UserPlus className="h-4 w-4" />Add new customer “{q.trim()}”</button>}
        </div>, document.body)}
      {adding && <NewCustomerDialog name={adding} onClose={() => setAdding(null)} onCreated={(c) => { onCreated(c); onChange(c.id); setAdding(null); setQ(null); }} />}
    </div>
  );
}
